import "../games.css";

import { useEffect, useRef, useState } from "react";

import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";

const GamesPage = () => {
  usePageTitle("Games");
  const [gameState, setGameState] = useState({
    credits: 1000,
    bet: 10,
    spinning: false,
    autoSpin: false,
    autoSpinCount: 0,
    freeSpins: 0,
    multiplier: 1,
    totalWins: 0,
    totalSpins: 0,
    reels: [],
    lastWin: 0,
  });

  const [message, setMessage] = useState("Good Luck!");
  const [showPaytable, setShowPaytable] = useState(false);
  const [winningMessage, setWinningMessage] = useState(false);
  const audioContextRef = useRef(null);

  const symbols = [
    { icon: "🦌", value: 500 }, 
    { icon: "🌲", value: 300 },  
    { icon: "🎅", value: 150 }, 
    { icon: "❄️", value: 100 }, 
    { icon: "🧣", value: 80 },  
    { icon: "🎿", value: 60 },  
    { icon: "🧤", value: 50 },  
    { icon: "⛄", value: 40 },  
  ];

  const wildSymbol = { icon: "🎰", value: 0, type: "wild" };
  const scatterSymbol = { icon: "⚡", value: 0, type: "scatter" };

  const paylines = [
    [1, 1, 1, 1, 1],
    [0, 0, 0, 0, 0],
    [2, 2, 2, 2, 2],
    [0, 1, 2, 1, 0],
    [2, 1, 0, 1, 2],
    [1, 0, 1, 2, 1],
    [1, 2, 1, 0, 1],
    [0, 0, 1, 2, 2],
    [2, 2, 1, 0, 0],
  ];

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext ||
      window.webkitAudioContext)();
    initReels();
    createParticles();
    const interval = setInterval(createParticles, 3000);
    return () => clearInterval(interval);
  }, []);

  const initReels = () => {
    const newReels = [];
    for (let i = 0; i < 5; i++) {
      const reel = [];
      for (let j = 0; j < 3; j++) {
        reel.push(getRandomSymbol());
      }
      newReels.push(reel);
    }
    setGameState((prev) => ({ ...prev, reels: newReels }));
  };

  const getRandomSymbol = () => {
    const rand = Math.random();
    if (rand < 0.05) return wildSymbol;
    if (rand < 0.1) return scatterSymbol;
    return symbols[Math.floor(Math.random() * symbols.length)];
  };

  const playSound = (frequency, duration, type = "sine") => {
    if (!audioContextRef.current) return;

    const oscillator = audioContextRef.current.createOscillator();
    const gainNode = audioContextRef.current.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContextRef.current.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;

    gainNode.gain.setValueAtTime(0.1, audioContextRef.current.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      audioContextRef.current.currentTime + duration
    );

    oscillator.start(audioContextRef.current.currentTime);
    oscillator.stop(audioContextRef.current.currentTime + duration);
  };

  const adjustBet = (amount) => {
    if (gameState.spinning) return;
    setGameState((prev) => ({
      ...prev,
      bet: Math.max(5, Math.min(100, prev.bet + amount)),
    }));
  };

  const spin = async () => {
    if (gameState.spinning) return;
    if (gameState.credits < gameState.bet && gameState.freeSpins === 0) {
      showMessage("❌ Not enough credits!");
      return;
    }

    setWinningMessage(false);

    setGameState((prev) => {
      const newState = {
        ...prev,
        spinning: true,
        totalSpins: prev.totalSpins + 1,
      };
      if (prev.freeSpins === 0) {
        newState.credits = prev.credits - prev.bet;
      } else {
        newState.freeSpins = prev.freeSpins - 1;
      }
      return newState;
    });

    const spinDuration = 2000;
    const spinInterval = 50;
    let elapsed = 0;

    const spinAnimation = setInterval(() => {
      const newReels = [];
      for (let i = 0; i < 5; i++) {
        const reel = [];
        for (let j = 0; j < 3; j++) {
          reel.push(getRandomSymbol());
        }
        newReels.push(reel);
      }
      // Bu kısım, animasyon için rastgele makaraları göstermek üzere güncellendi
      setGameState((prev) => ({ ...prev, reels: newReels }));

      playSound(200 + elapsed / 10, 0.05, "square");
      elapsed += spinInterval;

      if (elapsed >= spinDuration) {
        clearInterval(spinAnimation);
        finishSpin();
      }
    }, spinInterval);
  };

  const finishSpin = () => {
    const finalReels = [];
    for (let i = 0; i < 5; i++) {
      const reel = [];
      for (let j = 0; j < 3; j++) {
        reel.push(getRandomSymbol());
      }
      finalReels.push(reel);
    }

    const result = checkWins(finalReels);

    setGameState((prev) => {
      const newState = {
        ...prev,
        reels: finalReels,
        spinning: false,
      };

      if (prev.freeSpins > 0 && newState.freeSpins === 0) {
        newState.multiplier = 1;
      }

      if (result.totalWin > 0) {
        newState.credits = prev.credits + result.totalWin;
        newState.totalWins = prev.totalWins + result.totalWin;
        newState.lastWin = result.totalWin;

        if (result.totalWin >= prev.bet * 10) {
          showMessage(`🎊 BIG WIN! +${result.totalWin} CREDITS! 🎊`);
          playSound(800, 0.5, "triangle");
        } else {
          showMessage(`✨ WIN! +${result.totalWin} CREDITS! ✨`);
          playSound(600, 0.3, "sine");
        }
        setWinningMessage(true);
        setTimeout(() => setWinningMessage(false), 2000);
      } else {
        newState.lastWin = 0;
        showMessage(
          newState.freeSpins > 0
            ? `${newState.freeSpins} Free Spins Left!`
            : "Try Again!"
        );
        playSound(150, 0.2, "sawtooth");
      }

      return newState;
    });

    setGameState((prev) => {
      if (prev.autoSpin && prev.autoSpinCount > 0) {
        const newAutoSpinCount = prev.autoSpinCount - 1;
        setTimeout(() => {
          if (prev.autoSpin) {
            if (newAutoSpinCount === 0) {
              stopAutoSpin();
            } else {
              // Recursively call spin, but update the state first
              setGameState((p) => ({ ...p, autoSpinCount: newAutoSpinCount }));
              spin();
            }
          }
        }, 1000);
      }
      return prev;
    });
  };

  const checkWins = (reels) => {
    let totalWin = 0;
    const winningLines = [];

    paylines.forEach((line) => {
      const lineSymbols = line.map((row, col) => reels[col][row]);
      let firstSymbol = lineSymbols[0];

      let matchCount = 1;

      if (firstSymbol.type === "wild") {
        for (let i = 1; i < lineSymbols.length; i++) {
          if (lineSymbols[i].type !== "wild") {
            firstSymbol = lineSymbols[i];
            break;
          }
        }
      }

      for (let i = 1; i < lineSymbols.length; i++) {
        const currentSymbol = lineSymbols[i];

        if (currentSymbol.type === "wild") {
          matchCount++;
        } else if (currentSymbol.icon === firstSymbol.icon) {
          matchCount++;
        } else {
          break;
        }
      }

      if (matchCount >= 3) {
        const winSymbol =
          firstSymbol.type === "wild" ? symbols[0] : firstSymbol;
        const lineWin = winSymbol.value * matchCount * (gameState.bet / 10);
        totalWin += lineWin;
        winningLines.push({ symbols: line, win: lineWin });
      }
    });

    const scatterCount = reels
      .flat()
      .filter((s) => s.type === "scatter").length;
    if (scatterCount >= 3) {
      const scatterWin = gameState.bet * scatterCount * 5;
      totalWin += scatterWin;

      if (gameState.freeSpins === 0) {
        const freeSpinsAwarded = scatterCount * 3;
        setGameState((prev) => ({
          ...prev,
          freeSpins: prev.freeSpins + freeSpinsAwarded, // Mevcut free spin varsa ekle
          multiplier: 2,
        }));
        showMessage(`🎉 ${freeSpinsAwarded} FREE SPINS! 2x MULTIPLIER! 🎉`);
      }
    }

    return { totalWin: totalWin * gameState.multiplier, winningLines };
  };

  const startAutoSpin = (count) => {
    if (
      gameState.spinning ||
      (gameState.credits < gameState.bet && gameState.freeSpins === 0)
    )
      return;

    setGameState((prev) => ({
      ...prev,
      autoSpin: true,
      autoSpinCount: count,
    }));

    spin();
  };

  const stopAutoSpin = () => {
    setGameState((prev) => ({
      ...prev,
      autoSpin: false,
      autoSpinCount: 0,
    }));
  };

  const showMessage = (msg) => {
    setMessage(msg);
  };

  const resetGame = () => {
    setGameState((prev) => ({
      ...prev,
      credits: 1000,
      bet: 10,
      totalWins: 0,
      totalSpins: 0,
      freeSpins: 0,
      multiplier: 1,
      lastWin: 0,
    }));
    showMessage("Credits Reset!");
    initReels();
  };

  // Paytable'ı göster/gizle fonksiyonu
  const togglePaytable = () => {
    setShowPaytable((prev) => !prev);
    document.getElementById("paytableToggle").textContent = showPaytable
      ? "SHOW"
      : "HIDE";
  };

  // Notify formu için iskelet
  const handleNotify = (e) => {
    e.preventDefault();
    alert("Thank you for your interest! We will notify you.");
  };

  const createParticles = () => {
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        const particle = document.createElement("div");
        particle.innerHTML = ["💎", "⭐", "💚", "😍"][
          Math.floor(Math.random() * 4)
        ];
        particle.style.position = "fixed";
        particle.style.left = Math.random() * window.innerWidth + "px";
        particle.style.top = window.innerHeight + "px";
        particle.style.fontSize = Math.random() * 20 + 15 + "px";
        particle.style.pointerEvents = "none";
        particle.style.zIndex = "1";
        particle.style.animation = "floatUp 4s ease-out forwards";

        document.body.appendChild(particle);

        setTimeout(() => {
          particle.remove();
        }, 4000);
      }, i * 1000);
    }
  };

  const winRate =
    gameState.totalSpins > 0
      ? (
          (gameState.totalWins / (gameState.totalSpins * gameState.bet)) *
          100
        ).toFixed(1)
      : 0;

  return (
    <div className="games-page">
      <section className="page-header">
        <h1>🎮 GAMES COLLECTION 🎮</h1>
        <p style={{ color: "#66ff66", fontSize: "1.2rem" }}>
          Exciting games coming soon!
        </p>
        <Link to="/" className="back-button">
          ← BACK TO HOME
        </Link>
      </section>

      <div className={`container ${winningMessage ? "winning-animation" : ""}`}>
        <div className="header">
          <h1>🎰 polira.site 🎰</h1>
          <p>Advanced 5-Reel Slot Machine</p>
        </div>

        {/* Stats Bar  */}
        <div className="stats-bar">
          <div className="stat-box">
            <div className="stat-label">CREDITS</div>
            <div className="stat-value" id="credits">
              {gameState.credits}
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-label">BET</div>
            <div className="stat-value" id="bet">
              {gameState.bet}
            </div>
          </div>
          <div className="stat-box gold">
            <div className="stat-label" style={{ color: "#ffff88" }}>
              LAST WIN
            </div>
            <div className="stat-value gold" id="lastWin">
              {gameState.lastWin}
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-label">TOTAL WINS</div>
            <div className="stat-value" id="totalWins">
              {gameState.totalWins}
            </div>
          </div>
        </div>

        {/* Free Spins Banner */}
        <div
          id="freeSpinsBanner"
          className={`free-spins-banner ${
            gameState.freeSpins > 0 ? "" : "hidden"
          }`}
        >
          ⚡ FREE SPINS: <span id="freeSpinsCount">{gameState.freeSpins}</span>{" "}
          | MULTIPLIER: <span id="multiplierValue">{gameState.multiplier}</span>
          x ⚡
        </div>

        {/* Message  */}
        <div
          id="messageBox"
          className={`message-box ${winningMessage ? "win-highlight" : ""}`}
        >
          {message}
        </div>

        {/* Slot Machine  */}
        <div className="slot-machine">
          <div className="reels-container" id="reelsContainer">
            {gameState.reels.map((reel, reelIndex) => (
              <div
                key={reelIndex}
                className={`reel ${gameState.spinning ? "spinning" : ""}`}
              >
                {reel.map((symbol, symbolIndex) => (
                  <div
                    key={symbolIndex}
                    className={`symbol ${symbol.type || ""} ${
                      winningMessage ? "winning-symbol" : ""
                    }`}
                  >
                    {symbol.icon}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="paylines-indicator">💰 9 PAYLINES ACTIVE 💰</div>
        </div>

        {/* Controls  */}
        {!gameState.autoSpin && (
          <div className="controls">
            <button
              id="betMinus"
              onClick={() => adjustBet(-5)}
              disabled={gameState.spinning}
            >
              BET -
            </button>
            <button
              id="betPlus"
              onClick={() => adjustBet(5)}
              disabled={gameState.spinning}
            >
              BET +
            </button>
            <button
              id="spinButton"
              className="spin-button"
              onClick={spin}
              disabled={
                gameState.spinning ||
                (gameState.credits < gameState.bet && gameState.freeSpins === 0)
              }
            >
              🎰{" "}
              {gameState.freeSpins > 0
                ? `FREE SPIN (${gameState.freeSpins})`
                : "SPIN!"}
            </button>
            <button
              id="auto10"
              className="auto-button"
              onClick={() => startAutoSpin(10)}
              disabled={gameState.spinning || gameState.credits < gameState.bet}
            >
              AUTO x10
            </button>
            <button
              id="auto50"
              className="auto-button"
              onClick={() => startAutoSpin(50)}
              disabled={gameState.spinning || gameState.credits < gameState.bet}
            >
              AUTO x50
            </button>
          </div>
        )}

        {/* Auto Spin Stop Container */}
        {gameState.autoSpin && (
          <div id="stopAutoContainer" className="controls">
            <button className="stop-auto-button" onClick={stopAutoSpin}>
              STOP AUTO ({gameState.autoSpinCount})
            </button>
          </div>
        )}

        {/* Secondary Controls  */}
        <div className="secondary-controls">
          <button className="secondary-button" onClick={togglePaytable}>
            <span id="paytableToggle">{showPaytable ? "HIDE" : "SHOW"}</span>{" "}
            PAYTABLE
          </button>
          <button className="secondary-button" onClick={resetGame}>
            RESET CREDITS
          </button>
        </div>

        {/* Paytable  */}
        <div
          id="paytable"
          className={`paytable ${showPaytable ? "visible" : "hidden"}`}
        >
          <h2>💰 PAYTABLE 💰</h2>
          <div className="paytable-grid">
            {symbols.map((symbol) => (
              <div className="paytable-item" key={symbol.icon}>
                <div className="paytable-icon">{symbol.icon}</div>
                <div className="paytable-info">
                  <div
                    className="paytable-name"
                    style={{
                      color:
                        symbol.icon === "💎"
                          ? "#00ffff"
                          : symbol.icon === "7️⃣"
                          ? "#ff0000"
                          : symbol.icon === "🍒"
                          ? "#ff1493"
                          : symbol.icon === "🔔"
                          ? "#ffd700"
                          : "#ffff00",
                    }}
                  >
                    {symbol.icon === "💎"
                      ? "Diamond"
                      : symbol.icon === "7️⃣"
                      ? "Lucky Seven"
                      : symbol.icon === "🍒"
                      ? "Cherry"
                      : symbol.icon === "🔔"
                      ? "Bell"
                      : symbol.icon === "⭐"
                      ? "Star"
                      : symbol.icon === "🍋"
                      ? "Lemon"
                      : symbol.icon === "🍊"
                      ? "Orange"
                      : "Grape"}
                  </div>
                  <div className="paytable-value">
                    3x: {symbol.value} | 4x: {symbol.value * 2} | 5x:{" "}
                    {symbol.value * 3}
                  </div>
                </div>
              </div>
            ))}
            <div className="paytable-item">
              <div className="paytable-icon">{wildSymbol.icon}</div>
              <div className="paytable-info">
                <div className="paytable-name" style={{ color: "#00ff00" }}>
                  WILD
                </div>
                <div className="paytable-value">Substitutes for any symbol</div>
              </div>
            </div>
            <div className="paytable-item">
              <div className="paytable-icon">{scatterSymbol.icon}</div>
              <div className="paytable-info">
                <div className="paytable-name" style={{ color: "#ffff00" }}>
                  SCATTER
                </div>
                <div className="paytable-value">3+ triggers Free Spins</div>
              </div>
            </div>
          </div>

          <div className="bonus-features">
            <h3>⚡ BONUS FEATURES ⚡</h3>
            <ul>
              <li>
                🎰 <strong>WILD</strong>: Substitutes for any symbol except
                SCATTER
              </li>
              <li>
                ⚡ <strong>SCATTER</strong>: 3+ anywhere triggers FREE SPINS
                with 2x MULTIPLIER
              </li>
              <li>
                🎯 <strong>9 PAYLINES</strong>: Multiple ways to win on every
                spin!
              </li>
              <li>
                💎 <strong>BIG WINS</strong>: Wins 10x+ your bet trigger special
                animations!
              </li>
            </ul>
          </div>
        </div>

        {/* Statistics  */}
        <div className="statistics">
          <h3>📊 SESSION STATS 📊</h3>
          <div className="stats-grid">
            <div>
              <div style={{ color: "#66ff66", fontSize: "0.9rem" }}>
                Total Spins
              </div>
              <div
                style={{ fontSize: "1.5rem", fontWeight: "bold" }}
                id="totalSpins"
              >
                {gameState.totalSpins}
              </div>
            </div>
            <div>
              <div style={{ color: "#66ff66", fontSize: "0.9rem" }}>
                Total Won
              </div>
              <div
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  color: "#ffd700",
                }}
                id="totalWon"
              >
                {gameState.totalWins}
              </div>
            </div>
            <div>
              <div style={{ color: "#66ff66", fontSize: "0.9rem" }}>
                Win Rate
              </div>
              <div
                style={{ fontSize: "1.5rem", fontWeight: "bold" }}
                id="winRate"
              >
                {winRate}%
              </div>
            </div>
          </div>
        </div>

        {/* Footer  */}
        <div className="footer">
          <p>🎮 For Entertainment Only | No Real Money Involved 🎮</p>
          <p>© 2025 polira.site - Advanced 5-Reel Slot Machine</p>
        </div>
      </div>

      <div className="notify-section">
        <h3 style={{ marginBottom: "1rem", fontSize: "1.5rem" }}>
          Get Notified When We Launch!
        </h3>
        <form className="notify-input" onSubmit={handleNotify}>
          <input type="email" placeholder="Enter your email" required />
          <button type="submit" className="notify-button">
            NOTIFY ME
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
};

export default GamesPage;
